"use client";

import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Container,
  Paper,
  CircularProgress,
  SelectChangeEvent,
  Button,
} from "@mui/material";
import ContestantList from "./components/ContestantList";
import ScoringForm from "./components/ScoringForm";
import ResultsSummary from "./components/ResultSummary";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import { apiRemote, ISubmissionTcc } from "@/api";
import { useNavigate } from "react-router";
import { useUser } from "@/contexts/UserContext";

// Sample scoring criteria
const SCORING_CRITERIA = [{ id: "score", name: "Score", maxScore: process.env.MAX_SCORE || 200 }];

const steps = ["Select Contestant", "Score Performance", "Review Results"];

export default function ScoringSystem() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedContestant, setSelectedContestant] = useState<string | null>(
    null
  );
  const [selectedContestantSubmissions, setSelectedContestantSubmissions] =
    useState<ISubmissionTcc["data"]["data"] | null>(null);
  const [scores, setScores] = useState<Record<string, Record<string, number>>>(
    {}
  );
  const {
    submissions,
    setSubmissions,
    currentPageSubmission,
    setCurrentPageSubmission,
    pageSizeSubmission,
    setPageSizeSubmission,
    totalCountSubmission,
    setTotalCountSubmission,
  } = useThoughtCoCreate();
  const [loadingSubmission, setLoadingSubmission] = useState<boolean>(false);
  const navigate = useNavigate();
  const {user} =useUser();

  useEffect(() => {
    const savedStep = localStorage.getItem("activeStep");
    const savedContestant = localStorage.getItem("selectedContestant");
    const savedSubmissions = localStorage.getItem(
      "selectedContestantSubmissions"
    );

    if (savedStep) setActiveStep(Number(savedStep));
    if (savedContestant) setSelectedContestant(savedContestant);
    if (savedSubmissions)
      setSelectedContestantSubmissions(JSON.parse(savedSubmissions));
  }, []);

  const loadTCCSubmission = useCallback(
    async (current_page: number, pageSize: number) => {
      setLoadingSubmission(true);
      try {
        const response = await apiRemote.getListSubmissionTcc(1, 1000, user?.user_login);
        if (response?.data) {
          // setTotalCountSubmission(response.data.total);
          setSubmissions(response.data.data);
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu:", error);
      } finally {
        setLoadingSubmission(false);
      }
    },
    []
  );

  useEffect(() => {
    loadTCCSubmission(currentPageSubmission, pageSizeSubmission);
  }, [currentPageSubmission, pageSizeSubmission, loadTCCSubmission]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setCurrentPageSubmission(newPage);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const nextStep = prevActiveStep + 1;
      localStorage.setItem("activeStep", String(nextStep)); // Lưu trạng thái
      return nextStep;
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      const prevStep = prevActiveStep - 1;
      localStorage.setItem("activeStep", String(prevStep)); // Lưu trạng thái
      return prevStep;
    });
  };

  const handleContestantSelect = (user: string) => {
    setSelectedContestant(user);
    localStorage.setItem("selectedContestant", user);

    // 🟢 Tìm các bài nộp của thí sinh và lưu vào state + localStorage
    const contestantSubmissions = submissions
      .filter((submission) => submission.user === user)
      .reduce((acc: any, submission, index) => {
        acc = submission;
        return acc;
      }, {} as ISubmissionTcc["data"]["data"]);

    setSelectedContestantSubmissions(contestantSubmissions);
    localStorage.setItem(
      "selectedContestantSubmissions",
      JSON.stringify(contestantSubmissions)
    );

    handleNext();
  };

  const handleScoreSubmit = (
    contestant: string,
    contestantScores: Record<string, number>
  ) => {
    setScores((prevScores) => ({
      ...prevScores,
      [contestant]: contestantScores,
    }));
    handleNext();
  };

  const handleScoreAnother = () => {
    setSelectedContestant(null);
    setSelectedContestantSubmissions(null);
    setActiveStep(0);
    localStorage.setItem("activeStep", "0");
    localStorage.removeItem("selectedContestant");
    localStorage.removeItem("selectedContestantSubmissions");
  };

  const getStepContent = (step: number) => {
    if (step === 1 && (!selectedContestant || !selectedContestantSubmissions)) {
      // 🛑 Nếu mất dữ liệu, quay về bước 1
      setActiveStep(0);
      return (
        <Typography sx={{ textAlign: "center", color: "red" }}>
          Data lost, please select a contestant again.
        </Typography>
      );
    }
    switch (step) {
      case 0:
        return (
          <ContestantList
            contestants={submissions}
            onSelect={handleContestantSelect}
          />
        );
      case 1:
        return (
          <ScoringForm
            contestant={selectedContestantSubmissions!}
            criteria={SCORING_CRITERIA}
            onSubmit={(contestantScores: any) =>
              handleScoreSubmit(selectedContestant!, contestantScores)
            }
            onBack={handleBack}
            existingScores={scores[selectedContestant!] ?? undefined}
            handleNext={handleNext}
          />
        );
      case 2:
        return (
          <ResultsSummary
            contestants={submissions}
            criteria={SCORING_CRITERIA}
            scores={scores}
            onScoreAnother={handleScoreAnother}
          />
        );
      default:
        return "Unknown step";
    }
  };

  const handleViewResult = () => {
    localStorage.setItem("activeStep", "0");
    localStorage.removeItem("selectedContestant");
    localStorage.removeItem("selectedContestantSubmissions");
    navigate(`results`);
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mb: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Competition Scoring System
        </Typography>
        <Box sx={{ p: 1, display: "flex", justifyContent: "end" }}>
          <Button variant="outlined" onClick={handleViewResult}>
            View results
          </Button>
        </Box>
        {loadingSubmission ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ width: "100%", mb: 4 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {getStepContent(activeStep)}
          </>
        )}
      </Paper>
    </Container>
  );
}
